import { c as _c } from "react/compiler-runtime";
import React, { useRef } from 'react';
import { ConfigProvider as AntdConfigProvider } from 'antd';
import { isEqual } from 'lodash';

/**
 * Custom useMemo hook that supports a custom shouldUpdate function.
 * This is a replacement for rc-util/es/hooks/useMemo to avoid direct rc-* imports.
 *
 * Uses refs intentionally during render for memoization caching — this is a stable
 * pattern that doesn't need re-render triggers (the cache only changes when inputs change).
 */
function useMemoWithCustomCompare<Value, Condition>(getValue: () => Value, condition: Condition, shouldUpdate: (prev: Condition, next: Condition) => boolean): Value {
  /* eslint-disable react-hooks/refs -- Intentional: ref-based memoization cache that reads/writes during render. setState would cause infinite loops since the cached value is derived from props that create new objects each render. */
  const cacheRef = useRef<{
    value: Value;
    condition: Condition;
  } | null>(null);
  if (cacheRef.current === null || shouldUpdate(cacheRef.current.condition, condition)) {
    cacheRef.current = {
      value: getValue(),
      condition
    };
  }
  return cacheRef.current.value;
  /* eslint-enable react-hooks/refs */
}
import { SupportedLocales } from '@commutifi/constants/Locale';
import { rgbToRgba } from '@commutifi-fe/utils/src/colors';
import commutifiTheme from '../theme';
import { ANTD_CSS_VAR_KEY } from '../theme/cssVar';
import { defaultConfig, extractThemeFromDocumentStyle } from '../theme/context';
import { Themes } from '../theme/constants';
import defaultTheme from '../style/themes/default.module.scss';
import darkTheme from '../style/themes/dark.module.scss';
import enUS from '../locales/default';
import { Locale } from '../locales/index';
import { ConfigContext } from './context';
import './antd-reset.css';
import { ConfigProviderProps } from './props';
import { colors } from '../style/constants';
export type { Locale };
export { AntdConfigProvider };
const {
  getDesignToken
} = commutifiTheme;
function hexToRgbComponents(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return undefined;
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

// The danger-button hover shade has to stay legible in both themes, and it plays
// two contrast roles at once: white text sits ON it for solid danger buttons,
// while it IS the text/border color for the default and ghost danger variants.
// No single fixed shade clears AA in both themes, so resolve it per theme — a
// dark red (destructive-600) reads on the light theme's white ground, and the
// dark theme's already-brightened red (destructive-200, which that theme maps to
// #ef616b for text on dark) reads on its near-black ground.
const resolveDangerButtonHover = (currentTheme: `${Themes}`, dynamicColors: ReturnType<typeof extractThemeFromDocumentStyle>) => currentTheme === Themes.DARK ? dynamicColors.feedbackDestructive200 : dynamicColors.feedbackDestructive600;
export function ConfigProvider(t0) {
  const $ = _c(51);
  let children;
  let config;
  let t1;
  let t2;
  let theme;
  if ($[0] !== t0) {
    ({
      children,
      localeKey: t1,
      locale: t2,
      theme,
      ...config
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = config;
    $[3] = t1;
    $[4] = t2;
    $[5] = theme;
  } else {
    children = $[1];
    config = $[2];
    t1 = $[3];
    t2 = $[4];
    theme = $[5];
  }
  const localeKey = t1 === undefined ? SupportedLocales.enUS : t1;
  const locale = t2 === undefined ? enUS : t2;
  const appliedThemeRef = useRef(null);
  let t3;
  if ($[6] !== locale || $[7] !== localeKey) {
    t3 = {
      locale,
      localeKey
    };
    $[6] = locale;
    $[7] = localeKey;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const commutifiConfig = t3;
  let t4;
  if ($[9] !== commutifiConfig) {
    t4 = () => commutifiConfig;
    $[9] = commutifiConfig;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const memoedCommutifiConfig = useMemoWithCustomCompare(t4, commutifiConfig, _temp);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = extractThemeFromDocumentStyle();
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const [dynamicColors, setDynamicColors] = React.useState(t5);
  let t6;
  if ($[12] !== dynamicColors) {
    t6 = currentTheme => ({
      algorithm: currentTheme === Themes.DARK ? commutifiTheme.darkAlgorithm : commutifiTheme.defaultAlgorithm,
      token: {
        ...defaultConfig.token,
        colorPrimary: dynamicColors.primary400 || "",
        colorLink: dynamicColors.primary400 || "",
        colorLinkHover: dynamicColors.primary600 || "",
        colorLinkActive: dynamicColors.primary600 || "",
        colorSuccess: dynamicColors.feedbackSuccess400 || "",
        colorWarning: dynamicColors.feedbackWarning400 || "",
        colorText: dynamicColors.neutral600 || "",
        colorTextHeading: dynamicColors.neutral600 || "",
        colorTextSecondary: dynamicColors.neutral500 || "",
        colorTextPlaceholder: dynamicColors.neutral500 || "",
        colorError: dynamicColors.feedbackDestructive500 || "",
        colorTextDisabled: dynamicColors.neutral500 || "",
        colorPrimaryActive: dynamicColors.primary500 || "",
        colorPrimaryHover: dynamicColors.primary500 || "",
        colorErrorHover: dynamicColors.feedbackDestructive500 || "",
        colorTextDescription: dynamicColors.neutral500 || "",
        colorInfo: dynamicColors.accentBlue500 || "",
        gold: dynamicColors.accentGold500 || "",
        green: dynamicColors.accentGreen500 || "",
        ...(currentTheme !== Themes.DARK && {
          colorBgLayout: dynamicColors.backgroundColor || ""
        })
      },
      components: {
        Alert: {
          ...defaultConfig.components?.Alert,
          colorInfo: dynamicColors.neutral600,
          colorInfoBg: dynamicColors.neutral200,
          colorInfoBorder: dynamicColors.neutral300,
          colorWarning: dynamicColors.feedbackWarning400,
          colorWarningBg: dynamicColors.feedbackWarning100,
          colorWarningBorder: dynamicColors.feedbackWarning200,
          colorSuccess: dynamicColors.neutral600,
          colorSuccessBorder: dynamicColors.feedbackSuccess200,
          colorSuccessBg: dynamicColors.feedbackSuccess100,
          colorError: dynamicColors.feedbackDestructive400,
          colorErrorBg: dynamicColors.feedbackDestructive100,
          colorErrorBorder: "#B82608"
        },
        Breadcrumb: {
          ...defaultConfig.components?.Breadcrumb,
          linkColor: dynamicColors.neutral500 || ""
        },
        Button: {
          ...defaultConfig.components?.Button,
          defaultBorderColor: dynamicColors.neutral300,
          defaultShadow: `0px 2px 4px -2px rgba(${dynamicColors.neutral600}, 0.12)`,
          colorPrimaryTextHover: dynamicColors.primary600,
          defaultGhostColor: dynamicColors.neutral600,
          defaultGhostBorderColor: dynamicColors.neutral600,
          defaultHoverColor: dynamicColors.neutral500,
          defaultActiveColor: dynamicColors.primary400,
          colorError: dynamicColors.feedbackDestructive500,
          colorErrorHover: resolveDangerButtonHover(currentTheme, dynamicColors) || "",
          colorErrorBorderHover: resolveDangerButtonHover(currentTheme, dynamicColors) || "",
          colorLinkActive: dynamicColors.primary600
        },
        Checkbox: {
          ...defaultConfig.components?.Checkbox,
          colorPrimary: dynamicColors.primary350 || "",
          colorPrimaryHover: dynamicColors.primary400 || ""
        },
        Descriptions: {
          ...defaultConfig.components?.Descriptions,
          labelColor: dynamicColors.neutral500 || ""
        },
        Divider: {
          ...defaultConfig.components?.Divider,
          colorSplit: dynamicColors.neutral300 || ""
        },
        Empty: {
          ...defaultConfig.components?.Empty,
          colorTextDescription: dynamicColors.neutral500 || ""
        },
        InputNumber: {
          ...defaultConfig.components?.InputNumber,
          colorErrorOutline: dynamicColors.feedbackDestructive500 || ""
        },
        Menu: {
          ...defaultConfig.components?.Menu,
          groupTitleColor: dynamicColors.neutral500 || "",
          itemSelectedBg: rgbToRgba(dynamicColors.primary500, 0.3),
          itemSelectedColor: dynamicColors.primary500
        },
        Message: {
          ...defaultConfig.components?.Message,
          colorBgElevated: dynamicColors.colorBgLayout || "",
          contentBg: dynamicColors.neutral600Static || "",
          colorText: colors.white || ""
        },
        Modal: {
          ...defaultConfig.components?.Modal,
          ...(currentTheme === Themes.DARK && {
            colorBgMask: "rgba(0, 0, 0, 0.10)"
          })
        },
        Notification: {
          ...defaultConfig.components?.Notification,
          colorErrorBg: dynamicColors.feedbackDestructive100 || "",
          colorWarningBg: dynamicColors.feedbackWarning100 || "",
          colorSuccessBg: dynamicColors.feedbackSuccess100 || "",
          colorInfoBg: dynamicColors.primary100 || ""
        },
        Radio: {
          ...defaultConfig.components?.Radio,
          colorPrimary: dynamicColors.primary350 || "",
          colorBgContainerDisabled: dynamicColors.neutral300 || "",
          colorTextDisabled: dynamicColors.neutral400 || "",
          buttonCheckedBgDisabled: dynamicColors.neutral400 || "",
          buttonCheckedColorDisabled: dynamicColors.neutral500 || "",
          buttonBg: dynamicColors.neutral200 || "",
          buttonCheckedBg: dynamicColors.colorBgLayout || ""
        },
        Select: {
          ...defaultConfig.components?.Select
        },
        Skeleton: {
          ...defaultConfig.components?.Skeleton,
          gradientFromColor: currentTheme === Themes.DARK ? dynamicColors.neutral400 : dynamicColors.neutral200,
          gradientToColor: dynamicColors.neutral300 || ""
        },
        Statistic: {
          contentFontSize: 36,
          colorTextDescription: dynamicColors.neutral500 || ""
        },
        Switch: {
          ...defaultConfig.components?.Switch,
          colorPrimary: dynamicColors.primary350 || "",
          colorPrimaryHover: dynamicColors.primary400 || ""
        },
        Tabs: {
          itemColor: dynamicColors.neutral500 || "",
          itemHoverColor: dynamicColors.neutral600 || "",
          itemSelectedColor: dynamicColors.neutral600 || ""
        },
        Tag: {
          ...defaultConfig.components?.Tag,
          defaultBg: dynamicColors.neutral200Border,
          colorSuccess: dynamicColors.neutral600,
          colorSuccessBg: dynamicColors.feedbackSuccess200,
          colorSuccessBorder: dynamicColors.feedbackSuccess400,
          colorError: dynamicColors.neutral600,
          colorErrorBg: dynamicColors.feedbackDestructive200,
          colorErrorBorder: dynamicColors.feedbackDestructive400,
          colorWarning: dynamicColors.neutral600Static,
          colorWarningBg: dynamicColors.feedbackWarning300,
          colorWarningBorder: dynamicColors.feedbackWarning400,
          colorIcon: dynamicColors.neutral600 || ""
        },
        Timeline: {
          ...defaultConfig.components?.Timeline
        },
        Tooltip: {
          controlHeight: 32
        },
        Tree: {
          ...defaultConfig.components?.Tree,
          colorPrimary: dynamicColors.primary350 || "",
          colorPrimaryHover: dynamicColors.primary400 || ""
        }
      }
    });
    $[12] = dynamicColors;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const getAntdTheme = t6;
  let t7;
  if ($[14] !== getAntdTheme) {
    t7 = newTheme => {
      if (newTheme === appliedThemeRef.current) {
        return;
      }
      appliedThemeRef.current = newTheme;
      const globalToken = getDesignToken(getAntdTheme(newTheme));
      bb333: switch (newTheme) {
        case "dark":
        case Themes.DARK:
          {
            document.getElementsByTagName("html")[0].style.setProperty("color-scheme", "dark");
            document.getElementsByTagName("html")[0].setAttribute("data-theme", "dark");
            document.documentElement.style.setProperty("--color-bg-layout", hexToRgbComponents(globalToken.colorBgLayout) || "");
            document.documentElement.style.setProperty("--color-bg-container", hexToRgbComponents(globalToken.colorBgContainer) || "");
            document.documentElement.style.setProperty("--color-bg-elevated", hexToRgbComponents(globalToken.colorBgElevated) || "");
            Object.entries(darkTheme).forEach(_temp2);
            break bb333;
          }
        case "light":
        default:
          {
            document.getElementsByTagName("html")[0].style.setProperty("color-scheme", "light");
            document.getElementsByTagName("html")[0].setAttribute("data-theme", "light");
            Object.entries(defaultTheme).forEach(_temp3);
          }
      }
      setDynamicColors(extractThemeFromDocumentStyle());
    };
    $[14] = getAntdTheme;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const changeTheme = t7;
  let t8;
  if ($[16] !== changeTheme || $[17] !== theme?.appearance) {
    t8 = () => {
      changeTheme(theme?.appearance || Themes.LIGHT);
    };
    $[16] = changeTheme;
    $[17] = theme?.appearance;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = theme?.appearance;
  let t10;
  if ($[19] !== changeTheme || $[20] !== t9) {
    t10 = [changeTheme, t9];
    $[19] = changeTheme;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  React.useEffect(t8, t10);
  const currentTheme_0 = theme?.appearance || Themes.LIGHT;
  let t11;
  if ($[22] !== currentTheme_0 || $[23] !== getAntdTheme) {
    t11 = getAntdTheme(currentTheme_0);
    $[22] = currentTheme_0;
    $[23] = getAntdTheme;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const antdTheme = t11;
  const t12 = theme?.token;
  const t13 = antdTheme?.token;
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = {
      ...t12,
      ...t13
    };
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = antdTheme?.components;
  const t16 = theme?.components;
  let t17;
  if ($[28] !== t15 || $[29] !== t16) {
    t17 = {
      ...t15,
      ...t16
    };
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = theme?.cssVar;
  let t19;
  if ($[31] !== t18) {
    t19 = {
      ...t18,
      key: ANTD_CSS_VAR_KEY
    };
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== antdTheme || $[34] !== t14 || $[35] !== t17 || $[36] !== t19 || $[37] !== theme) {
    t20 = {
      ...antdTheme,
      ...theme,
      token: t14,
      components: t17,
      cssVar: t19
    };
    $[33] = antdTheme;
    $[34] = t14;
    $[35] = t17;
    $[36] = t19;
    $[37] = theme;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== config || $[40] !== locale || $[41] !== t20) {
    t21 = {
      theme: t20,
      locale,
      ...config
    };
    $[39] = config;
    $[40] = locale;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const antdConfig = t21;
  let t22;
  if ($[43] !== antdConfig) {
    t22 = () => antdConfig;
    $[43] = antdConfig;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const memoedAntdConfig = useMemoWithCustomCompare(t22, antdConfig, _temp4);
  let t23;
  if ($[45] !== children || $[46] !== memoedAntdConfig) {
    t23 = <AntdConfigProvider {...memoedAntdConfig}>{children}</AntdConfigProvider>;
    $[45] = children;
    $[46] = memoedAntdConfig;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== memoedCommutifiConfig || $[49] !== t23) {
    t24 = <ConfigContext value={memoedCommutifiConfig}>{t23}</ConfigContext>;
    $[48] = memoedCommutifiConfig;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  return t24;
}
function _temp4(prevConfig_0, currentConfig_0) {
  const prevKeys_0 = Object.keys(prevConfig_0) as (keyof typeof antdConfig)[];
  const currentKeys_0 = Object.keys(currentConfig_0) as (keyof typeof antdConfig)[];
  return prevKeys_0.length !== currentKeys_0.length || !isEqual(prevConfig_0, currentConfig_0);
}
function _temp3(t0) {
  const [key_0, value] = t0;
  document.documentElement.style.setProperty(key_0, value);
}
function _temp2(t0) {
  const [key_1, value_0] = t0;
  document.documentElement.style.setProperty(key_1, value_0);
}
function _temp(prevConfig, currentConfig) {
  const prevKeys = Object.keys(prevConfig) as (keyof typeof commutifiConfig)[];
  const currentKeys = Object.keys(currentConfig) as (keyof typeof commutifiConfig)[];
  return prevKeys.length !== currentKeys.length || prevKeys.some(key => prevConfig[key] !== currentConfig[key]);
}
export default ConfigProvider;